@file:Suppress("TooManyFunctions")

package top.wcpe.allincore.ui.web.mock

internal typealias MockRoutes = Map<String, MockRoute>

internal data class MockRoute(val branches: List<MockBranch>, val default: MockScript)

internal data class MockBranch(val whenFields: Map<String, Any?>, val script: MockScript)

internal data class MockScript(
    val response: Map<String, Any>?,
    val error: Map<String, Any>?,
    val timeout: Boolean,
    val delayMs: Long,
)

internal sealed interface MockDecision {
    data object None : MockDecision
    data class Success(val payload: Map<String, Any>, val delayMs: Long) : MockDecision
    data class Failure(val payload: Map<String, Any>, val delayMs: Long) : MockDecision
    data class Timeout(val delayMs: Long) : MockDecision
}

internal sealed interface MockMatchLocation {
    data class Branch(val index: Int) : MockMatchLocation
    data object Default : MockMatchLocation
    data object Unregistered : MockMatchLocation
}

internal data class MockResolution(val decision: MockDecision, val location: MockMatchLocation)

internal class MockSchemaException(val code: String, val location: String, detail: String) :
    IllegalArgumentException("$code：$detail（位置：$location）")

internal fun parseMockScenario(text: String): MockRoutes {
    val root = JsonParser(text).parse().asObject("$")
    root.requireOnly(ROOT_FIELDS, "$")
    val routes = root.requireField("routes", "$").asObject("$.routes")
    return routes.mapValues { (path, routeValue) ->
        validateRoutePath(path)
        parseRoute(routeValue.asObject(routeLocation(path)), routeLocation(path))
    }
}

internal fun resolveMock(scenario: MockRoutes, path: String, payload: Map<String, Any?>): MockDecision =
    resolveMockRoute(scenario, path, payload).decision

internal fun resolveMockRoute(scenario: MockRoutes, path: String, payload: Map<String, Any?>): MockResolution {
    val route = scenario[path]
        ?: return MockResolution(MockDecision.None, MockMatchLocation.Unregistered)
    route.branches.forEachIndexed { index, branch ->
        if (branch.whenFields.all { (key, expected) -> payload[key] == expected }) {
            return MockResolution(branch.script.toDecision(), MockMatchLocation.Branch(index))
        }
    }
    return MockResolution(route.default.toDecision(), MockMatchLocation.Default)
}

private fun parseRoute(value: Map<String, Any?>, location: String): MockRoute {
    value.requireOnly(ROUTE_FIELDS, location)
    val branches = if (!value.containsKey("match")) {
        emptyList()
    } else {
        when (val raw = value["match"]) {
            is List<*> -> raw.mapIndexed { index, branch ->
                val branchLocation = "$location.match[$index]"
                parseBranch(branch.asObject(branchLocation), branchLocation)
            }
            else -> schemaFailure(MOCK_SCHEMA_TYPE, "$location.match", "match 必须是数组")
        }
    }
    return MockRoute(
        branches = branches,
        default = parseScript(value, location, ROUTE_FIELDS),
    )
}

private fun parseBranch(value: Map<String, Any?>, location: String): MockBranch {
    value.requireOnly(BRANCH_FIELDS, location)
    val whenFields = value.requireField("when", location).asObject("$location.when").normalizeJsonObject()
    return MockBranch(
        whenFields = whenFields,
        script = parseScript(value, location, BRANCH_FIELDS),
    )
}

@Suppress("UNCHECKED_CAST")
private fun parseScript(value: Map<String, Any?>, location: String, allowedFields: Set<String>): MockScript {
    value.requireOnly(allowedFields, location)
    val response = value.optionalObject("response", location) as Map<String, Any>?
    val error = value.optionalObject("error", location) as Map<String, Any>?
    val timeout = if (!value.containsKey("timeout")) {
        false
    } else {
        when (val raw = value["timeout"]) {
            is Boolean -> raw
            else -> schemaFailure(MOCK_SCHEMA_TYPE, "$location.timeout", "timeout 必须是布尔值")
        }
    }
    val delayMs = if (value.containsKey("delayMs")) parseDelay(value["delayMs"], "$location.delayMs") else 0L
    if (!timeout && error == null && response == null) {
        schemaFailure(MOCK_SCHEMA_EMPTY_SCRIPT, location, "Mock 剧本必须声明 timeout、error 或 response")
    }
    return MockScript(response = response, error = error, timeout = timeout, delayMs = delayMs)
}

private fun parseDelay(value: Any?, location: String): Long {
    if (value !is JsonNumber) {
        schemaFailure(MOCK_SCHEMA_TYPE, location, "delayMs 必须是非负整数毫秒")
    }
    val raw = value.raw
    if (raw.startsWith('-') || raw.any { it == '.' || it == 'e' || it == 'E' }) {
        schemaFailure(MOCK_SCHEMA_DELAY, location, "delayMs 必须是非负整数毫秒")
    }
    return raw.toLongOrNull()
        ?: schemaFailure(MOCK_SCHEMA_DELAY, location, "delayMs 超出可支持的整数范围")
}

private fun MockScript.toDecision(): MockDecision = when {
    timeout -> MockDecision.Timeout(delayMs)
    error != null -> MockDecision.Failure(error, delayMs)
    response != null -> MockDecision.Success(response, delayMs)
    else -> MockDecision.None
}

private fun validateRoutePath(path: String) {
    val segments = path.split('.')
    val canonical = path.isNotEmpty() &&
        path.length <= MAX_MOCK_ROUTE_PATH_LENGTH &&
        path == path.trim() &&
        segments.all { segment ->
            segment.isNotEmpty() && segment == segment.trim() && segment.none(Char::isWhitespace)
        }
    if (!canonical) {
        schemaFailure(MOCK_SCHEMA_PATH, routeLocation(path), "Mock 路由必须是规范 namespace.handler 路径")
    }
}

private fun Map<String, Any?>.requireOnly(allowed: Set<String>, location: String) {
    val unknown = keys.firstOrNull { it !in allowed } ?: return
    schemaFailure(MOCK_SCHEMA_UNKNOWN_FIELD, "$location.$unknown", "存在未声明字段 $unknown")
}

private fun Map<String, Any?>.requireField(name: String, location: String): Any? {
    if (!containsKey(name)) {
        schemaFailure(MOCK_SCHEMA_REQUIRED_FIELD, "$location.$name", "缺少必填字段 $name")
    }
    return get(name)
}

private fun Map<String, Any?>.optionalObject(name: String, location: String): Map<String, Any?>? =
    if (!containsKey(name)) null else get(name).asObject("$location.$name").normalizeJsonObject()

private fun Map<String, Any?>.normalizeJsonObject(): Map<String, Any?> =
    entries.associate { (key, value) -> key to normalizeJsonValue(value) }

private fun normalizeJsonValue(value: Any?): Any? = when (value) {
    is JsonNumber -> value.value
    is Map<*, *> -> value.entries.associate { (key, item) -> key.toString() to normalizeJsonValue(item) }
    is List<*> -> value.map(::normalizeJsonValue)
    else -> value
}

@Suppress("UNCHECKED_CAST")
private fun Any?.asObject(location: String): Map<String, Any?> {
    if (this !is Map<*, *>) {
        schemaFailure(MOCK_SCHEMA_TYPE, location, "字段必须是 JSON 对象")
    }
    return this as Map<String, Any?>
}

private fun routeLocation(path: String): String = "$.routes[$path]"

private fun schemaFailure(code: String, location: String, detail: String): Nothing =
    throw MockSchemaException(code, location, detail)

private data class JsonNumber(val raw: String, val value: Double)

private class JsonParser(private val text: String) {
    private var index: Int = 0

    fun parse(): Any? {
        skipWhitespace()
        val value = parseValue("$")
        skipWhitespace()
        if (index != text.length) fail("JSON 末尾存在多余内容")
        return value
    }

    private fun parseValue(location: String): Any? {
        skipWhitespace()
        if (index >= text.length) fail("JSON 提前结束")
        return when (text[index]) {
            '{' -> parseObject(location)
            '[' -> parseArray(location)
            '"' -> parseString()
            't' -> parseLiteral("true", true)
            'f' -> parseLiteral("false", false)
            'n' -> parseLiteral("null", null)
            '-', in '0'..'9' -> parseNumber()
            else -> fail("无法识别的 JSON 值")
        }
    }

    private fun parseObject(location: String): Map<String, Any?> {
        expect('{')
        skipWhitespace()
        val result = linkedMapOf<String, Any?>()
        if (peek('}')) {
            index += 1
            return result
        }
        while (true) {
            skipWhitespace()
            if (!peek('"')) fail("JSON 对象键必须是字符串")
            val key = parseString()
            val childLocation = "$location.$key"
            if (result.containsKey(key)) {
                schemaFailure(MOCK_SCHEMA_DUPLICATE_KEY, childLocation, "JSON 对象键重复")
            }
            skipWhitespace()
            expect(':')
            result[key] = parseValue(childLocation)
            skipWhitespace()
            when {
                peek(',') -> index += 1
                peek('}') -> {
                    index += 1
                    return result
                }
                else -> fail("JSON 对象缺少逗号或右花括号")
            }
        }
    }

    private fun parseArray(location: String): List<Any?> {
        expect('[')
        skipWhitespace()
        val result = mutableListOf<Any?>()
        if (peek(']')) {
            index += 1
            return result
        }
        while (true) {
            result += parseValue("$location[${result.size}]")
            skipWhitespace()
            when {
                peek(',') -> index += 1
                peek(']') -> {
                    index += 1
                    return result
                }
                else -> fail("JSON 数组缺少逗号或右方括号")
            }
        }
    }

    private fun parseString(): String {
        expect('"')
        val out = StringBuilder()
        while (index < text.length) {
            val ch = text[index++]
            when (ch) {
                '"' -> return out.toString()
                '\\' -> out.append(parseEscape())
                else -> {
                    if (ch.code < CONTROL_CHARACTER_LIMIT) fail("JSON 字符串包含未转义控制字符")
                    out.append(ch)
                }
            }
        }
        fail("JSON 字符串缺少结束引号")
    }

    private fun parseEscape(): Char {
        if (index >= text.length) fail("JSON 转义提前结束")
        return when (val escaped = text[index++]) {
            '"' -> '"'
            '\\' -> '\\'
            '/' -> '/'
            'b' -> '\b'
            'f' -> '\u000C'
            'n' -> '\n'
            'r' -> '\r'
            't' -> '\t'
            'u' -> parseUnicodeEscape()
            else -> fail("不支持的 JSON 转义：$escaped")
        }
    }

    private fun parseUnicodeEscape(): Char {
        if (index + UNICODE_ESCAPE_LENGTH > text.length) fail("Unicode 转义提前结束")
        val hex = text.substring(index, index + UNICODE_ESCAPE_LENGTH)
        index += UNICODE_ESCAPE_LENGTH
        return hex.toIntOrNull(JSON_HEX_RADIX)?.toChar() ?: fail("Unicode 转义非法")
    }

    private fun parseNumber(): JsonNumber {
        val start = index
        if (peek('-')) index += 1
        if (peek('0')) {
            index += 1
        } else {
            consumeDigits(required = true)
        }
        if (peek('.')) {
            index += 1
            consumeDigits(required = true)
        }
        if (peek('e') || peek('E')) {
            index += 1
            if (peek('+') || peek('-')) index += 1
            consumeDigits(required = true)
        }
        val raw = text.substring(start, index)
        val value = raw.toDoubleOrNull()
        if (value == null || !value.isFinite()) fail("JSON 数字非法")
        return JsonNumber(raw, value)
    }

    private fun consumeDigits(required: Boolean) {
        val start = index
        while (index < text.length && text[index].isDigit()) index += 1
        if (required && start == index) fail("JSON 数字缺少数字")
    }

    private fun <T> parseLiteral(literal: String, value: T): T {
        if (!text.startsWith(literal, index)) fail("JSON 字面量非法")
        index += literal.length
        return value
    }

    private fun skipWhitespace() {
        while (index < text.length && text[index] in JSON_WHITESPACE) index += 1
    }

    private fun expect(expected: Char) {
        if (!peek(expected)) fail("JSON 期望字符 $expected")
        index += 1
    }

    private fun peek(expected: Char): Boolean = index < text.length && text[index] == expected

    private fun fail(message: String): Nothing {
        schemaFailure(MOCK_JSON_INVALID, "$@$index", message)
    }
}

internal const val MAX_MOCK_ROUTE_PATH_LENGTH = 1024

private val ROOT_FIELDS = setOf("routes")
private val ROUTE_FIELDS = setOf("match", "response", "error", "timeout", "delayMs")
private val BRANCH_FIELDS = setOf("when", "response", "error", "timeout", "delayMs")
private val JSON_WHITESPACE = setOf(' ', '\t', '\r', '\n')
private const val CONTROL_CHARACTER_LIMIT = 0x20
private const val UNICODE_ESCAPE_LENGTH = 4
private const val JSON_HEX_RADIX = 16

internal const val MOCK_JSON_INVALID = "MOCK_JSON_INVALID"
internal const val MOCK_SCHEMA_UNKNOWN_FIELD = "MOCK_SCHEMA_UNKNOWN_FIELD"
internal const val MOCK_SCHEMA_DUPLICATE_KEY = "MOCK_SCHEMA_DUPLICATE_KEY"
internal const val MOCK_SCHEMA_REQUIRED_FIELD = "MOCK_SCHEMA_REQUIRED_FIELD"
internal const val MOCK_SCHEMA_TYPE = "MOCK_SCHEMA_TYPE"
internal const val MOCK_SCHEMA_PATH = "MOCK_SCHEMA_PATH"
internal const val MOCK_SCHEMA_DELAY = "MOCK_SCHEMA_DELAY"
internal const val MOCK_SCHEMA_EMPTY_SCRIPT = "MOCK_SCHEMA_EMPTY_SCRIPT"
